import {
  ImagesIcon,
  MegaphoneIcon,
  NewspaperClippingIcon,
  ShapesIcon,
  StrategyIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

/**
 * The identity mark for a first-class object type.
 *
 * Mora runs two icon registers on purpose, and the split is the whole idea:
 *
 *   • **Places** — Home, Insights, Discover, Publishing — get a flat monochrome
 *     line icon. They are where you are.
 *   • **Objects** — Campaigns, Creatives, Briefs, Workflows, Personas — get
 *     this: the same line mark, but in the object's own hue on a soft wash of
 *     that hue. They are things you accumulate.
 *
 * Both registers are line drawings at the same stroke weight; only HUE
 * separates them. That is deliberate and replaced a filled-tile treatment on
 * 2026-08-24 — see `--object-mark-*` in `src/index.css` for why the tile lost.
 *
 * Because the mark belongs to the OBJECT and not to the sidebar, the same glyph
 * is meant to appear wherever that object does — the nav row, the page header
 * beside the H1, an object's card. That repetition is what turns the colour
 * into a wayfinding cue instead of decoration: you recognise the teal notebook
 * as "Briefs" before you read the word, on any surface.
 *
 * Reference: `docs/research/competitors/marketer-com/OBSERVED-2026-08-12-sidebar-and-brief-cards.md`
 * §1. Mark values live in `src/index.css` as `--object-mark-*`, never inline,
 * and are defined per theme. They deliberately remain separate from the wider
 * `--object-glyph-*` artwork used by agent marks and Briefs' banner/globe.
 *
 * NOT rendered in the page header or the breadcrumb — see `PageHeaderManager`
 * for why: a mark is for choosing among options, and both of those surfaces
 * are statements about where you already are.
 */
export type ObjectGlyphName =
  'campaigns' | 'briefs' | 'studio' | 'assets' | 'workflows' | 'personas';

/** Exported so other surfaces can read an object's canonical icon instead of
 *  hardcoding one — design-system rule 26. The composer's `+` menu reads
 *  `GLYPH_ICON.assets` for its Assets row. */
export const GLYPH_ICON = {
  campaigns: MegaphoneIcon,
  briefs: NewspaperClippingIcon,
  studio: ShapesIcon,
  // `ImagesIcon` (stacked photos), not `ImageSquareIcon`. Founder, 2026-08-21:
  // "not the square. The square looks too old school and not good." Stacked
  // plates also say the true thing — Assets is a LIBRARY of many things, which
  // a single framed photo never conveyed. Panorama was the other candidate and
  // was rejected: it implies wide landscape photography specifically, while
  // this page holds video, documents and product shots too.
  //
  // Changed HERE, at the canonical map, so every surface that reads
  // `GLYPH_ICON.assets` moves together — that is the entire reason rule 26
  // exists.
  assets: ImagesIcon,
  workflows: StrategyIcon,
  personas: UsersThreeIcon,
} as const satisfies Record<ObjectGlyphName, unknown>;

// ONE register, six objects. This map used to be split: four objects on flat
// brand-colour tiles with a charcoal mark, and Assets/Personas on the
// iridescent dark tile with a near-white mark — two treatments inside one
// component, which is exactly the drift the design system calls its #1 smell.
// All six now read `--object-mark-*`, whose derivation lives in `src/index.css`.
// `--object-glyph-*` is deliberately NOT referenced here any more; it stays put
// because AgentMark and the Briefs banner still use it, and navigation identity
// must not move when that artwork does.
const MARK_COLOR = {
  campaigns: 'var(--object-mark-campaigns)',
  briefs: 'var(--object-mark-briefs)',
  studio: 'var(--object-mark-studio)',
  assets: 'var(--object-mark-assets)',
  workflows: 'var(--object-mark-workflows)',
  personas: 'var(--object-mark-personas)',
} as const satisfies Record<ObjectGlyphName, string>;

/**
 * Container and mark scale together. The mark sits at ~80% of the container —
 * larger than the 70% the filled tile used, because the wash behind it is a
 * soft hint rather than an edge, so the mark itself has to carry the shape.
 * At 70% on a wash the glyph read as small and lost inside its own halo.
 */
const SIZES = {
  // Corner radius tracks Apple's ~22% of icon width rather than a fixed token,
  // which is what makes a small mark read as an app icon instead of a rounded
  // box. At 20px the previous 6px (30%) was visibly too round.
  sm: { box: 'size-3.5 rounded-object-glyph', icon: 'size-3' },
  // The Core register (Home / Insights / Discover / Products / Publishing) is a
  // 16px line glyph, and matching it is the entire point of this treatment: a
  // 16px mark inside a 20px wash sits in the SAME drawing register as its
  // neighbours, where the old filled tile did not.
  md: { box: 'size-5 rounded-md', icon: 'size-4' },
  lg: { box: 'size-6 rounded-md', icon: 'size-5' },
} as const;

export function ObjectGlyph({
  name,
  size = 'md',
  isActive = false,
  className,
}: {
  name: ObjectGlyphName;
  size?: keyof typeof SIZES;
  isActive?: boolean;
  className?: string;
}): JSX.Element {
  const Icon = GLYPH_ICON[name];
  const scale = SIZES[size];

  return (
    <span
      aria-hidden="true"
      className={cn('inline-grid shrink-0 place-items-center', scale.box, className)}
      data-testid={`${name}-object-glyph`}
      // Dynamic CSS custom properties — the one sanctioned use of an inline
      // style. The VALUES stay in index.css so visual identity stays swappable.
      // The wash is derived from the mark colour with `color-mix` rather than
      // being a second hand-picked token, so the two cannot drift apart.
      style={{
        backgroundColor: isActive
          ? 'transparent'
          : `color-mix(in oklab, ${MARK_COLOR[name]} 14%, transparent)`,
        color: MARK_COLOR[name],
      }}
    >
      {/* `regular` (outline), matching the Core line icons exactly. The founder
          ruled "should just be an outline" on 2026-08-18 against a filled tile;
          on a bare mark it is not merely preferred, it is the whole idea. */}
      <Icon weight="regular" className={scale.icon} />
    </span>
  );
}
